import React, { useContext, useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { CartContext } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import API from '../config/api';
import logowhite from '../assets/logowhite.png';
import { STORIES } from '../data/storiesData';

export default function Navbar() {
  const { user, logout } = useContext(AuthContext);
  const { cartCount } = useContext(CartContext);
  const { lang, setLang, t } = useLanguage();

  const [showProfile, setShowProfile] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  const [menuData, setMenuData] = useState({});
  const [categories, setCategories] = useState([]);
  const profileRef = useRef(null);
  const menuTimer = useRef(null);
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [topStrip, setTopStrip] = useState({
    enabled: true,
    freeShippingMinAmount: 10000,
    offerText: '',
    intervalSeconds: 4,
    contactEmail: 'support@kaaleenhaus.com',
  });
  const [topStripIndex, setTopStripIndex] = useState(0);
  const storyHighlights = STORIES.slice(0, 5);

  const isHome = location.pathname === '/';
  const isHomeAtTop = isHome && !isScrolled;
  const isHomeScrolled = isHome && isScrolled;

  // EUR conversion helper (1 EUR ≈ 91 INR)
  const toEur = (inr) => Math.round(Number(inr) / 91);

  useEffect(() => {
    fetch(API.nav.menu)
      .then(r => r.json())
      .then(d => { if (d.status === 'success') { setMenuData(d.menu || {}); setCategories(d.categories || []); } })
      .catch(() => {});

    fetch(API.settings.hero)
      .then(r => r.json())
      .then(d => {
        if (d.status !== 'success' || !d.data) return;
        setTopStrip({
          enabled: d.data.top_strip_enabled !== false,
          freeShippingMinAmount: Number(d.data.free_shipping_min_amount || 10000),
          offerText: (d.data.top_strip_offer_text || '').trim(),
          intervalSeconds: Number(d.data.top_strip_interval_seconds) > 1 ? Number(d.data.top_strip_interval_seconds) : 4,
          contactEmail: (d.data.contact_email || 'support@kaaleenhaus.com').trim(),
        });
      })
      .catch(() => {});
  }, []);

  const formatInrAmount = (amount) => {
    const n = Number(amount);
    if (!Number.isFinite(n) || n < 0) return '10,000';
    return Math.round(n).toLocaleString('en-IN');
  };

  const freeShippingLabel = lang === 'de'
    ? `KOSTENLOSER VERSAND AB €${toEur(topStrip.freeShippingMinAmount).toLocaleString('de-DE')}`
    : `FREE SHIPPING ON ORDERS ABOVE ₹${formatInrAmount(topStrip.freeShippingMinAmount)}`;

  const topStripItems = [
    { text: freeShippingLabel },
    ...(topStrip.offerText ? [{ text: topStrip.offerText.toUpperCase() }] : []),
    { text: lang === 'de' ? 'KONTAKTIEREN SIE UNS' : 'CONTACT US', to: '/contact#contact-form' },
  ];

  useEffect(() => {
    if (!topStrip.enabled || topStripItems.length <= 1) return;
    const intervalMs = Math.max(2, Number(topStrip.intervalSeconds) || 4) * 1000;
    const timer = setInterval(() => {
      setTopStripIndex(prev => (prev + 1) % topStripItems.length);
    }, intervalMs);
    return () => clearInterval(timer);
  }, [topStrip.enabled, topStrip.intervalSeconds, topStripItems.length]);

  useEffect(() => {
    if (topStripIndex >= topStripItems.length) setTopStripIndex(0);
  }, [topStripIndex, topStripItems.length]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) setShowProfile(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isHome) { setIsScrolled(false); return; }
    const onScroll = () => setIsScrolled(window.scrollY > 100);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isHome]);

  const handleMenuEnter = (key) => { clearTimeout(menuTimer.current); setActiveMenu(key); };
  const handleMenuLeave = () => { menuTimer.current = setTimeout(() => setActiveMenu(null), 200); };

  // Nav labels — translated when in German
  const navItems = [
    { key: 'shop',    label: lang === 'de' ? 'Teppiche'        : 'Shop',       link: '/shop' },
    { key: 'style',   label: lang === 'de' ? 'Stil'            : 'Style',      link: '/shop?view=style' },
    { key: 'stories', label: lang === 'de' ? 'Geschichten'     : 'Stories',    link: '/stories' },
    { key: 'blog',    label: 'Blog',                                            link: '/blog' },
    { key: 'custom',  label: lang === 'de' ? 'Maßanfertigung'  : 'Custom',     link: '/custom' },
    { key: 'contact', label: lang === 'de' ? 'Kontakt'         : 'Contact Us', link: '/contact' },
    { key: 'trade',   label: lang === 'de' ? 'Großhandel'      : 'Trade',      link: '/trade' },
  ];

  const hasDropdown = (key) => {
    if (key === 'stories') return true;
    if (key === 'shop' && categories.length > 0) return true;
    return menuData[key] && menuData[key].length > 0;
  };

  const [mobileExpanded, setMobileExpanded] = useState(null);

  const navTextColor = isHomeAtTop ? '#ffffff' : 'var(--text-primary)';
  const navStrokeColor = isHomeAtTop ? '#ffffff' : 'var(--text-primary)';
  const navMutedIconColor = isHomeAtTop ? 'rgba(37,37,37,0.75)' : 'var(--text-muted)';
  const navBorderColor = isHomeAtTop ? 'rgba(255,255,255,0.55)' : 'var(--border-light)';
  const navSearchBg = isHomeAtTop ? 'rgba(255,255,255,0.92)' : '#ffffff';
  const navSearchTextColor = isHomeAtTop ? '#2d2d2d' : 'var(--text-primary)';
  const navSearchPlaceholder = isHomeAtTop ? 'rgba(45,45,45,0.55)' : 'rgba(95,95,95,0.7)';

  const headerStyle = { position: isHome ? 'fixed' : 'sticky', top: 0, zIndex: 100, left: 0, right: 0 };

  const navShellStyle = isHomeAtTop
    ? { background: 'transparent', borderBottom: '1px solid transparent', position: 'relative', transition: 'background 0.35s ease, border-color 0.35s ease, box-shadow 0.35s ease' }
    : isHomeScrolled
      ? { background: 'rgba(255,255,255,0.15)', borderBottom: '1px solid rgba(255,255,255,0.18)', position: 'relative', backdropFilter: 'blur(20px) saturate(180%)', WebkitBackdropFilter: 'blur(20px) saturate(180%)', boxShadow: '0 14px 34px rgba(0,0,0,0.14)', transition: 'background 0.35s ease' }
      : { backgroundColor: '#fff', borderBottom: '1px solid var(--border-light)', position: 'relative' };

  // ── Language Toggle Button ────────────────────────────────
  const LangToggle = () => (
    <button
      onClick={() => setLang(lang === 'de' ? 'en' : 'de')}
      title={lang === 'de' ? 'Switch to English' : 'Auf Deutsch wechseln'}
      style={{
        display: 'flex', alignItems: 'center', gap: '4px',
        padding: '5px 10px',
        borderRadius: '999px',
        border: `1px solid ${navBorderColor}`,
        background: isHomeAtTop ? 'rgba(255,255,255,0.1)' : 'transparent',
        color: navTextColor,
        fontSize: '11px',
        fontWeight: '700',
        letterSpacing: '1px',
        cursor: 'pointer',
        transition: 'all 0.25s ease',
        whiteSpace: 'nowrap',
      }}
    >
      {lang === 'de' ? '🇬🇧 EN' : '🇩🇪 DE'}
    </button>
  );

  return (
    <header style={headerStyle}>
      {/* Top strip */}
      {topStrip.enabled && (
        <div className="nav-topstrip" style={{ backgroundColor: 'var(--bg-dark)', color: 'var(--text-on-dark)', textAlign: 'center', padding: '8px 0', fontSize: '11px', letterSpacing: '1.5px', fontWeight: '300', minHeight: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {topStripItems[topStripIndex]?.to ? (
            <Link key={`topstrip-${topStripIndex}`} className="nav-topstrip-msg" to={topStripItems[topStripIndex].to} style={{ color: 'var(--accent-gold)', textDecoration: 'underline' }}>
              {topStripItems[topStripIndex].text}
            </Link>
          ) : (
            <span key={`topstrip-${topStripIndex}`} className="nav-topstrip-msg">{topStripItems[topStripIndex]?.text}</span>
          )}
        </div>
      )}

      {/* Main Nav */}
      <div style={navShellStyle}>
        <div className="nav-main" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 40px' }}>

          {/* Hamburger */}
          <button className="mobile-menu-btn" onClick={() => setMobileMenu(!mobileMenu)} style={{ display: 'none', padding: '5px' }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={navStrokeColor} strokeWidth="2"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
          </button>

          {/* Brand */}
          <Link to="/" className="nav-brand" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <img src={logowhite} alt="Kaaleen Haus" className="brand-logo" style={{ height: '40px', objectFit: 'contain' }} />
            <span className="brand-text" style={{ fontSize: '1.2rem', fontWeight: '600', letterSpacing: '3px', color: navTextColor }}>KAALEEN HAUS</span>
          </Link>

          {/* Center Nav */}
          <nav className="nav-center" style={{ display: 'flex', gap: '28px', fontSize: '12px', fontWeight: '500', letterSpacing: '1.5px', textTransform: 'uppercase' }}>
            {navItems.map(item => (
              <div key={item.key} onMouseEnter={() => handleMenuEnter(item.key)} onMouseLeave={handleMenuLeave} style={{ position: 'relative' }}>
                <Link to={item.link} className="nav-hover" style={{ color: navTextColor, paddingBottom: '5px', borderBottom: activeMenu === item.key ? '2px solid var(--accent-gold)' : '2px solid transparent', transition: 'border-color 0.2s, color 0.2s' }}>
                  {item.label}
                </Link>
              </div>
            ))}
            {user && <Link to="/orders" className="nav-hover" style={{ color: navTextColor }}>{lang === 'de' ? 'Bestellungen' : 'Orders'}</Link>}
            {user?.buyer_type === 'admin' && <Link to="/admin" className="nav-hover" style={{ color: 'var(--accent-gold)' }}>Admin</Link>}
          </nav>

          {/* Right Icons */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* Search */}
            <div className="nav-search" style={{ position: 'relative' }}>
              <input className="nav-search-input" type="text" placeholder={lang === 'de' ? 'Suchen...' : 'Search...'} style={{ width: '160px', padding: '8px 32px 8px 14px', border: `1px solid ${navBorderColor}`, borderRadius: '25px', fontSize: '12px', fontFamily: 'var(--font-sans)', color: navSearchTextColor, background: navSearchBg, outline: 'none', transition: 'width 0.3s', ['--nav-placeholder-color']: navSearchPlaceholder }} onFocus={e => e.target.style.width='220px'} onBlur={e => e.target.style.width='160px'} />
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={navMutedIconColor} strokeWidth="2" style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)' }}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            </div>

            {/* Language Toggle — hidden on mobile, available inside drawer */}
            <span className="desktop-lang-toggle"><LangToggle /></span>

            {/* Profile */}
            <div ref={profileRef} style={{ position: 'relative' }}>
              {user ? (
                <>
                  <div onClick={() => setShowProfile(!showProfile)} style={{ width: '30px', height: '30px', borderRadius: '50%', background: 'linear-gradient(135deg, var(--accent-gold), var(--accent-copper))', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                    <span style={{ color: '#fff', fontSize: '12px', fontWeight: '600' }}>{user.name?.[0]?.toUpperCase()}</span>
                  </div>
                  {showProfile && (
                    <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 8px)', backgroundColor: '#fff', border: '1px solid var(--border-light)', padding: '16px 20px', borderRadius: '8px', boxShadow: '0 8px 30px rgba(0,0,0,0.12)', minWidth: '200px', zIndex: 50 }}>
                      <p style={{ margin: '0 0 4px', fontSize: '14px', fontWeight: '500' }}>{user.name}</p>
                      <p style={{ margin: '0 0 12px', fontSize: '11px', color: 'var(--accent-gold)', textTransform: 'uppercase', letterSpacing: '1px' }}>{user.buyer_type}</p>
                      <Link to="/orders" onClick={() => setShowProfile(false)} style={{ display: 'block', padding: '10px 0', fontSize: '13px', borderTop: '1px solid var(--border-light)' }}>📦 {lang === 'de' ? 'Meine Bestellungen' : 'My Orders'}</Link>
                      {user.buyer_type === 'admin' && <Link to="/admin" onClick={() => setShowProfile(false)} style={{ display: 'block', padding: '10px 0', fontSize: '13px', borderTop: '1px solid var(--border-light)', fontWeight: '500', color: 'var(--accent-gold)' }}>⚙️ {lang === 'de' ? 'Admin-Dashboard' : 'Admin Dashboard'}</Link>}
                      <Link to="/cart" onClick={() => setShowProfile(false)} style={{ display: 'block', padding: '10px 0', fontSize: '13px', borderTop: '1px solid var(--border-light)' }}>🛒 {lang === 'de' ? 'Mein Warenkorb' : 'My Cart'} {cartCount > 0 && `(${cartCount})`}</Link>
                      <span onClick={() => { logout(); setShowProfile(false); }} style={{ display: 'block', padding: '10px 0', fontSize: '13px', color: '#e74c3c', cursor: 'pointer', borderTop: '1px solid var(--border-light)' }}>{lang === 'de' ? 'Abmelden' : 'Sign Out'}</span>
                    </div>
                  )}
                </>
              ) : (
                <Link to="/login"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={navStrokeColor} strokeWidth="1.5"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg></Link>
              )}
            </div>

            {/* Cart */}
            <Link to="/cart" style={{ position: 'relative' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={navStrokeColor} strokeWidth="1.5"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
              {cartCount > 0 && <span style={{ position: 'absolute', top: '-6px', right: '-8px', background: 'var(--accent-gold)', color: '#fff', fontSize: '9px', fontWeight: '600', borderRadius: '50%', width: '16px', height: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{cartCount}</span>}
            </Link>
          </div>
        </div>

        {/* ═══════ MEGA MENU ═══════ */}
        {activeMenu && hasDropdown(activeMenu) && (
          <div className="mega-menu" onMouseEnter={() => handleMenuEnter(activeMenu)} onMouseLeave={handleMenuLeave}
            style={{ position: 'absolute', left: 0, right: 0, top: '100%', backgroundColor: '#fff', borderTop: '2px solid var(--border-light)', boxShadow: '0 20px 50px rgba(0,0,0,0.1)', zIndex: 200, animation: 'megaSlide 0.25s ease' }}>

            {activeMenu === 'shop' ? (
              <div style={{ display: 'flex', padding: '40px 60px', justifyContent: 'space-between', maxWidth: '1400px', margin: '0 auto' }}>
                <div style={{ minWidth: '160px' }}>
                  <h4 style={{ fontSize: '11px', letterSpacing: '2px', textTransform: 'uppercase', color: '#1b1c15', marginBottom: '18px', fontWeight: '600' }}>{lang === 'de' ? 'Teppiche' : 'Rugs'}</h4>
                  <Link to="/shop" onClick={() => setActiveMenu(null)} className="mega-link" style={{ display: 'block', fontSize: '12px', padding: '5px 0', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>{lang === 'de' ? 'Alle Teppiche' : 'View All Rugs'}</Link>
                  {categories.map(cat => (
                    <Link key={cat.id} to={`/shop?category=${cat.id}`} onClick={() => setActiveMenu(null)} className="mega-link" style={{ display: 'block', fontSize: '12px', padding: '5px 0', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>{cat.name}</Link>
                  ))}
                </div>
                <div style={{ minWidth: '130px' }}>
                  <h4 style={{ fontSize: '11px', letterSpacing: '2px', textTransform: 'uppercase', color: '#1b1c15', marginBottom: '18px', fontWeight: '600' }}>{lang === 'de' ? 'Größe' : 'Size'}</h4>
                  {['2x3 FT', '3x5 FT', '4x6 FT', '5x8 FT', '6x9 FT', '8x10 FT', '9x12 FT', '10x14 FT', '12x15 FT', 'Small', 'Medium', 'Large', 'Oversize'].map(s => (
                    <Link key={s} to={`/shop?sizes=${encodeURIComponent(s)}`} onClick={() => setActiveMenu(null)} className="mega-link" style={{ display: 'block', fontSize: '12px', padding: '5px 0', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>{s}</Link>
                  ))}
                </div>
                <div style={{ minWidth: '130px' }}>
                  <h4 style={{ fontSize: '11px', letterSpacing: '2px', textTransform: 'uppercase', color: '#1b1c15', marginBottom: '18px', fontWeight: '600' }}>{lang === 'de' ? 'Farben' : 'Colors'}</h4>
                  {['Blue', 'Red', 'Green', 'Yellow', 'Ivory / White', 'Grey', 'Black', 'Orange', 'Pink', 'Purple', 'Brown', 'Beige', 'Multi Color'].map(c => (
                    <Link key={c} to={`/shop?colors=${encodeURIComponent(c)}`} onClick={() => setActiveMenu(null)} className="mega-link" style={{ display: 'block', fontSize: '12px', padding: '5px 0', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>{c}</Link>
                  ))}
                </div>
                <div style={{ minWidth: '130px' }}>
                  <h4 style={{ fontSize: '11px', letterSpacing: '2px', textTransform: 'uppercase', color: '#1b1c15', marginBottom: '18px', fontWeight: '600' }}>{lang === 'de' ? 'Raum' : 'Room'}</h4>
                  {['Living Room', 'Dining Room', 'Bedroom', 'Kids Room', 'Outdoor/Indoor'].map(r => (
                    <Link key={r} to={`/shop?rooms=${encodeURIComponent(r)}`} onClick={() => setActiveMenu(null)} className="mega-link" style={{ display: 'block', fontSize: '12px', padding: '5px 0', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>{r}</Link>
                  ))}
                  <h4 style={{ fontSize: '11px', letterSpacing: '2px', textTransform: 'uppercase', color: '#1b1c15', marginTop: '25px', marginBottom: '18px', fontWeight: '600' }}>{lang === 'de' ? 'Form' : 'Shape'}</h4>
                  {['Rectangle', 'Irregular', 'Round', 'Runner', 'Oval', 'Square'].map(s => (
                    <Link key={s} to={`/shop?shapes=${encodeURIComponent(s)}`} onClick={() => setActiveMenu(null)} className="mega-link" style={{ display: 'block', fontSize: '12px', padding: '5px 0', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>{s}</Link>
                  ))}
                </div>
                <div style={{ minWidth: '130px' }}>
                  <h4 style={{ fontSize: '11px', letterSpacing: '2px', textTransform: 'uppercase', color: '#1b1c15', marginBottom: '18px', fontWeight: '600' }}>{lang === 'de' ? 'Material' : 'Material'}</h4>
                  {['Wool', 'Wool & Bamboo Silk', 'Wool & Silk', 'Silk', 'Viscose', 'Jute & Hemp', 'Cotton', 'Polyester', 'Afghan Wool', 'Acrylic'].map(m => (
                    <Link key={m} to={`/shop?materials=${encodeURIComponent(m)}`} onClick={() => setActiveMenu(null)} className="mega-link" style={{ display: 'block', fontSize: '12px', padding: '5px 0', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>{m}</Link>
                  ))}
                </div>
                <div style={{ minWidth: '130px' }}>
                  <h4 style={{ fontSize: '11px', letterSpacing: '2px', textTransform: 'uppercase', color: '#1b1c15', marginBottom: '18px', fontWeight: '600' }}>{lang === 'de' ? 'Verarbeitung' : 'Construction'}</h4>
                  {['Hand Knotted', 'Hand Tufted', 'Hand Loom', 'Flat Weaves', 'Shag', 'Wall Décor'].map(c => (
                    <Link key={c} to={`/shop?constructions=${encodeURIComponent(c)}`} onClick={() => setActiveMenu(null)} className="mega-link" style={{ display: 'block', fontSize: '12px', padding: '5px 0', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>{c}</Link>
                  ))}
                </div>
              </div>
            ) : activeMenu === 'stories' ? (
              <div className="stories-mega-wrap" style={{ maxWidth: '1380px', margin: '0 auto', padding: '32px 44px 36px' }}>
                <div className="stories-mega-head" style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '16px', marginBottom: '18px' }}>
                  <div>
                    <span style={{ display: 'inline-block', fontSize: '10px', letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--accent-gold)', marginBottom: '6px', fontWeight: '600' }}>{lang === 'de' ? 'Redaktionell' : 'Editorial'}</span>
                    <h4 style={{ margin: 0, fontSize: '24px', color: '#17182b', fontFamily: 'var(--font-serif)', fontWeight: '500' }}>{lang === 'de' ? 'Die Geschichten hinter jedem Teppich' : 'The Stories Behind Every Rug'}</h4>
                  </div>
                  <Link to="/stories" onClick={() => setActiveMenu(null)} style={{ fontSize: '12px', letterSpacing: '1px', textTransform: 'uppercase', color: '#6f5a2f', textDecoration: 'underline', textUnderlineOffset: '4px' }}>{lang === 'de' ? 'Alle Geschichten' : 'View All Stories'}</Link>
                </div>
                <div className="stories-mega-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: '12px' }}>
                  {storyHighlights.map((story, index) => {
                    const storyImage = story.slug === 'founder' && story.founderPortrait ? story.founderPortrait : story.cardImage;
                    return (
                      <Link key={story.slug} to={`/stories/${story.slug}`} onClick={() => setActiveMenu(null)} className="stories-mega-card" style={{ textDecoration: 'none', border: '1px solid #ece7dd', borderRadius: '10px', overflow: 'hidden', background: '#fff', color: 'inherit', transition: 'transform 0.25s ease, box-shadow 0.25s ease' }}>
                        <div style={{ height: '180px', background: `url(${storyImage}) center/cover` }}></div>
                        <div style={{ padding: '10px 10px 12px' }}>
                          <span style={{ fontSize: '10px', color: '#a9803d', letterSpacing: '1px', fontWeight: '700' }}>0{index + 1}</span>
                          <p style={{ margin: '4px 0 2px', fontSize: '13px', color: '#15162a', fontWeight: '600' }}>{story.navLabel}</p>
                          <p style={{ margin: 0, fontSize: '11px', color: '#6a6d83', lineHeight: '1.35' }}>{story.navSub}</p>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', padding: '40px 60px', gap: '50px', maxWidth: '1400px', margin: '0 auto' }}>
                {(menuData[activeMenu] || []).map((col, i) => (
                  <div key={i} style={{ minWidth: '160px' }}>
                    <h4 style={{ fontSize: '11px', letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--accent-gold)', marginBottom: '18px', fontWeight: '600' }}>{col.title}</h4>
                    {(col.items || []).map((item, j) => (
                      <Link key={j} to={item.link || '#'} onClick={() => setActiveMenu(null)} className="mega-link" style={{ display: 'block', fontSize: '13px', padding: '5px 0', color: 'var(--text-secondary)', transition: 'color 0.2s' }}>{item.label}</Link>
                    ))}
                  </div>
                ))}
                {(menuData[activeMenu] || []).some(c => c.image_url) && (
                  <div style={{ marginLeft: 'auto', flexShrink: 0 }}>
                    {(menuData[activeMenu] || []).filter(c => c.image_url).slice(0, 1).map((c, i) => (
                      <div key={i} style={{ width: '240px', height: '200px', borderRadius: '8px', background: `url(${c.image_url}) center/cover`, position: 'relative', overflow: 'hidden' }}>
                        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '20px', background: 'linear-gradient(transparent, rgba(0,0,0,0.6))' }}>
                          <span style={{ color: '#fff', fontSize: '12px', fontWeight: '600', letterSpacing: '1px', textTransform: 'uppercase' }}>Explore →</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ═══════ MOBILE DRAWER ═══════ */}
      {mobileMenu && (
        <div style={{ backgroundColor: '#fff', borderBottom: '1px solid var(--border-light)', padding: '10px 0', maxHeight: '75vh', overflow: 'auto' }}>
          {navItems.map(item => (
            <div key={item.key}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', fontSize: '13px', fontWeight: '500', letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer', borderBottom: '1px solid var(--border-light)' }}
                onClick={() => hasDropdown(item.key) ? setMobileExpanded(mobileExpanded === item.key ? null : item.key) : null}>
                <Link to={item.link} onClick={() => !hasDropdown(item.key) && setMobileMenu(false)}>{item.label}</Link>
                {hasDropdown(item.key) && <span style={{ color: 'var(--accent-gold)', fontSize: '16px' }}>{mobileExpanded === item.key ? '−' : '+'}</span>}
              </div>
              {mobileExpanded === item.key && hasDropdown(item.key) && (
                <div style={{ padding: '10px 20px 10px 35px', backgroundColor: '#fafafa', borderBottom: '1px solid var(--border-light)' }}>
                  {item.key === 'shop' && categories.map(cat => (
                    <div key={cat.id}>
                      <Link to={`/shop?category=${cat.id}`} onClick={() => setMobileMenu(false)} style={{ display: 'block', padding: '6px 0', fontSize: '13px', fontWeight: '500' }}>{cat.name}</Link>
                    </div>
                  ))}
                  {item.key === 'stories' && storyHighlights.map((story) => (
                    <Link key={story.slug} to={`/stories/${story.slug}`} onClick={() => setMobileMenu(false)} style={{ display: 'block', padding: '8px 0', fontSize: '12px', color: 'var(--text-secondary)', borderBottom: '1px dashed #e6e1d8' }}>
                      <span style={{ display: 'block', fontWeight: '600', color: '#1f2134', marginBottom: '2px' }}>{story.navLabel}</span>
                      <span style={{ fontSize: '11px', color: '#75788d' }}>{story.navSub}</span>
                    </Link>
                  ))}
                  {(menuData[item.key] || []).map((col, i) => (
                    <div key={i} style={{ marginTop: '10px' }}>
                      <span style={{ fontSize: '10px', letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--accent-gold)', fontWeight: '600' }}>{col.title}</span>
                      {(col.items || []).map((link, j) => (
                        <Link key={j} to={link.link || '#'} onClick={() => setMobileMenu(false)} style={{ display: 'block', padding: '4px 0', fontSize: '12px', color: 'var(--text-secondary)' }}>{link.label}</Link>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          {user && <Link to="/orders" onClick={() => setMobileMenu(false)} style={{ display: 'block', padding: '14px 20px', fontSize: '13px', fontWeight: '500', letterSpacing: '1px', textTransform: 'uppercase', borderBottom: '1px solid var(--border-light)' }}>{lang === 'de' ? 'Meine Bestellungen' : 'My Orders'}</Link>}
          {user?.buyer_type === 'admin' && <Link to="/admin" onClick={() => setMobileMenu(false)} style={{ display: 'block', padding: '14px 20px', fontSize: '13px', fontWeight: '500', letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--accent-gold)', borderBottom: '1px solid var(--border-light)' }}>⚙️ Admin Panel</Link>}
          {/* Mobile lang toggle */}
          <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border-light)' }}>
            <button onClick={() => { setLang(lang === 'de' ? 'en' : 'de'); setMobileMenu(false); }} style={{ background: 'none', border: '1px solid var(--border-light)', borderRadius: '999px', padding: '6px 14px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', color: 'var(--text-primary)' }}>
              {lang === 'de' ? '🇬🇧 Switch to English' : '🇩🇪 Auf Deutsch wechseln'}
            </button>
          </div>
        </div>
      )}

      <style>{`
        .nav-hover { transition: color 0.25s ease; }
        .nav-hover:hover { color: var(--accent-gold) !important; }
        .mega-link:hover { color: var(--accent-gold) !important; }
        .stories-mega-card:hover { transform: translateY(-3px); box-shadow: 0 14px 32px rgba(26,31,46,0.12); }
        .nav-search-input::placeholder { color: var(--nav-placeholder-color); opacity: 1; }
        .nav-topstrip-msg { animation: navTopStripFade 0.35s ease; }
        @keyframes megaSlide { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes navTopStripFade { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
        @media (max-width: 1180px) { .stories-mega-grid { grid-template-columns: repeat(3, minmax(0, 1fr)) !important; } }
        @media (max-width: 768px) {
          .nav-center { display: none !important; }
          .nav-search { display: none !important; }
          .mobile-menu-btn { display: block !important; }
          .desktop-lang-toggle { display: none !important; }
          .nav-topstrip { padding: 5px 10px !important; font-size: 9px !important; letter-spacing: 1px !important; }
          .nav-main { padding: 10px 12px !important; }
          .brand-logo { height: 30px !important; }
          .brand-text { white-space: nowrap !important; }
        }
        @media (max-width: 380px) { .brand-logo { height: 24px !important; } .brand-text { font-size: 0.75rem !important; letter-spacing: 1.5px !important; white-space: nowrap !important; } }
      `}</style>
    </header>
  );
}
